import PropTypes from "prop-types";
import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";

// @mui icons
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const DropZone = styled(Box)(({ theme, isDragActive, hasFile, error }) => ({
  border: `2px dashed ${error ? theme.palette.error.main : 
    isDragActive ? theme.palette.primary.main : 
    hasFile ? theme.palette.success.main : 
    theme.palette.grey[400]}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: error ? theme.palette.error.light : 
    isDragActive ? theme.palette.primary.light : 
    hasFile ? theme.palette.success.light : 
    theme.palette.grey[100],
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  }
}));

function AttachmentForm({ formData, onFileChange }) {
  const { formField, values, errors, touched, setFieldValue } = formData;
  const { file } = formField;
  const [isDragActive, setIsDragActive] = useState(false);
  const [preview, setPreview] = useState(null);

  const allowedFileTypes = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

  const handleFileChange = (file, name) => {
    if (file) {
      const fileExtension = "." + file.name.split('.').pop().toLowerCase();
      if (!allowedFileTypes.includes(fileExtension)) {
        if (setFieldValue) {
          setFieldValue(name, null);
          setFieldValue('error', 'Only image files are allowed (.jpg, .jpeg, .png, .gif, .bmp, .webp)');
        }
        setPreview(null);
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      if (setFieldValue) {
        setFieldValue(name, file);
        setFieldValue('error', '');
      }
      if (onFileChange) {
        onFileChange(name, file);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile, file.name);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleRemoveFile = () => {
    setPreview(null);
    if (setFieldValue) {
      setFieldValue(file.name, null);
      setFieldValue('error', '');
    }
    if (onFileChange) {
      onFileChange(file.name, null);
    }
  };

  return (
    <MDBox>
      <MDBox lineHeight={0}>
        <MDTypography variant="h5">المرفقات</MDTypography>
        <MDTypography variant="button" color="text">
          اسحب وأفلت الصورة أو انقر للتحميل
        </MDTypography>
      </MDBox>
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              accept={allowedFileTypes.join(',')}
              onChange={(e) => handleFileChange(e.target.files[0], file.name)}
            />
            <label htmlFor="file-upload" style={{ width: '100%' }}>
              <DropZone
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                isDragActive={isDragActive}
                hasFile={!!preview}
                error={Boolean(errors.file && touched.file)}
              >
                {preview ? (
                  <Box position="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                    />
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemoveFile();
                      }}
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: -12,
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'error.dark',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                    <MDTypography variant="body2" color="textSecondary">
                      {isDragActive ? 'أفلت الملف هنا' : 'اسحب وأفلت أو انقر للتحميل'}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary" display="block">
                      الملفات المدعومة: {allowedFileTypes.join(', ')}
                    </MDTypography>
                  </Box>
                )}
              </DropZone>
            </label>
            {errors.file && touched.file && (
              <MDTypography variant="caption" color="error">
                {errors.file}
              </MDTypography>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

AttachmentForm.propTypes = {
  formData: PropTypes.shape({
    formField: PropTypes.object.isRequired,
    values: PropTypes.object.isRequired,
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }).isRequired,
  onFileChange: PropTypes.func,
};

export default AttachmentForm;
